import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../authentication/Auth';
import { analyticsClient } from './client';
import {
  acquisitionForLocation,
  analyticsEventForPath,
  browserFamily,
  deviceCategoryForWidth,
  productForPath,
  transitionEventsForNavigation,
} from './routeRules';

export default function RouteTracker() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const portalId = Number(user?.PortalId ?? user?.portal_id ?? user?.portalId);
  const acquisitionTracked = useRef(false);
  const lastInteractionAt = useRef(Date.now());
  const previousPath = useRef(null);
  const previousUserId = useRef(null);
  const lastPageViewAt = useRef(0);
  const lastPageViewPath = useRef(null);

  useEffect(() => {
    const shouldEnable = isAuthenticated && portalId === 1;
    const currentUserId = user?.UserId ?? user?.userId ?? user?.id ?? null;
    if (previousUserId.current !== currentUserId) {
      analyticsClient.resetIdentity();
      acquisitionTracked.current = false;
      previousUserId.current = currentUserId;
    }
    analyticsClient.setEnabled(shouldEnable);
  }, [isAuthenticated, portalId, user]);

  useEffect(() => {
    if (!isAuthenticated || portalId !== 1) return;
    const now = Date.now();
    if (location.pathname === lastPageViewPath.current && now - lastPageViewAt.current < 2000) {
      return;
    }
    lastPageViewAt.current = now;
    lastPageViewPath.current = location.pathname;
    const product = productForPath(location.pathname);
    const acquisition = acquisitionTracked.current
      ? {}
      : acquisitionForLocation(location.search, document.referrer);
    analyticsClient.track('page_view', {
      product,
      path: location.pathname,
      properties: {
        navigation_type: 'route',
        device_category: deviceCategoryForWidth(window.innerWidth),
        browser_family: browserFamily(window.navigator.userAgent),
        ...acquisition,
      },
    });
    acquisitionTracked.current = true;
    const journeyEvent = analyticsEventForPath(location.pathname);
    if (journeyEvent) {
      analyticsClient.track(journeyEvent, {
        product: journeyEvent === 'subscription_page_viewed' ? 'general' : product,
        path: location.pathname,
      });
    }
    transitionEventsForNavigation(previousPath.current, location.pathname, location.state || {})
      .forEach((eventName) => analyticsClient.track(eventName, {
        product,
        path: location.pathname,
        properties: { previous_path: previousPath.current },
      }));
    previousPath.current = location.pathname;
  }, [isAuthenticated, portalId, location.pathname, location.search, location.state]);

  useEffect(() => {
    if (!isAuthenticated || portalId !== 1) return undefined;
    const markInteraction = () => { lastInteractionAt.current = Date.now(); };
    const interactionEvents = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    interactionEvents.forEach((name) => window.addEventListener(name, markInteraction, { passive: true }));
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible' && Date.now() - lastInteractionAt.current <= 60000) {
        analyticsClient.track('active_time', {
          product: productForPath(window.location.pathname),
          path: window.location.pathname,
          properties: { active_seconds: 30 },
        });
      }
    }, 30000);
    return () => {
      window.clearInterval(timer);
      interactionEvents.forEach((name) => window.removeEventListener(name, markInteraction));
    };
  }, [isAuthenticated, portalId]);

  useEffect(() => {
    if (!isAuthenticated || portalId !== 1) return undefined;
    const requestInterceptor = axios.interceptors.request.use((config) => {
      config.analyticsStartedAt = Date.now();
      return config;
    });
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = String(error.config?.url || '');
        let apiPath = null;
        try { apiPath = new URL(url, window.location.origin).pathname; } catch (_) {}
        const eventName = /score|scoring|evaluat/i.test(apiPath || '') ? 'scoring_error' : 'api_error';
        analyticsClient.track(eventName, {
          product: productForPath(window.location.pathname),
          path: window.location.pathname,
          properties: {
            api_path: apiPath,
            http_status: Number(error.response?.status) || null,
            duration_ms: error.config?.analyticsStartedAt
              ? Math.max(0, Date.now() - error.config.analyticsStartedAt)
              : null,
          },
        });
        return Promise.reject(error);
      }
    );

    const reportMediaError = (event) => {
      const tag = String(event.target?.tagName || '').toLowerCase();
      if (tag !== 'audio' && tag !== 'video') return;
      analyticsClient.track('audio_error', {
        product: productForPath(window.location.pathname),
        path: window.location.pathname,
        properties: { media_type: tag },
      });
    };
    window.addEventListener('error', reportMediaError, true);

    const navigation = performance.getEntriesByType?.('navigation')?.[0];
    if (navigation) {
      analyticsClient.track('page_performance', {
        product: productForPath(window.location.pathname),
        path: window.location.pathname,
        properties: {
          load_ms: Math.round(navigation.loadEventEnd || navigation.duration || 0),
          dom_content_loaded_ms: Math.round(navigation.domContentLoadedEventEnd || 0),
        },
      });
    }

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
      window.removeEventListener('error', reportMediaError, true);
    };
  }, [isAuthenticated, portalId]);

  return null;
}
